import React, { memo, ReactNode, useCallback, useMemo } from "react";
import colors from "app/colors";
import {
  HStack,
  InputGroup,
  InputLeftAddon,
  Select,
  VStack,
  Text,
} from "@chakra-ui/react";
import {
  DATE_RANGE_TYPE,
  titleForDateRangeType,
} from "modules/utils/Constants";
import DateInput from "../../_shared/date-input";
import moment from "moment";
import useSearchFilter from "modules/hooks/use-search-filter";
import { SearchFilter, SearchFilterType } from "gql/graphql";
import { CalendarIcon } from "modules/icons";

interface IProps {
  title: string;
  startDateSearchFilterType: SearchFilterType;
  endDateSearchFilterType: SearchFilterType;
  dateRangeSearchFilterType: SearchFilterType;
  newSearchFilters: SearchFilter[];
  setNewSearchFilters: React.Dispatch<React.SetStateAction<SearchFilter[]>>;
  icon?: ReactNode;
}

const FiltersDrawerDateRangeSelector = ({
  title,
  startDateSearchFilterType,
  endDateSearchFilterType,
  dateRangeSearchFilterType,
  newSearchFilters,
  setNewSearchFilters,
  icon = <CalendarIcon fill={colors.blackSecondary} boxSize={5} />,
}: IProps) => {
  const { getStartDate } = useSearchFilter();

  const startDate = useMemo(
    () =>
      newSearchFilters.find(
        (filter) => filter.type === startDateSearchFilterType,
      )?.instantValue,
    [newSearchFilters, startDateSearchFilterType],
  );

  const endDate = useMemo(
    () =>
      newSearchFilters.find((filter) => filter.type === endDateSearchFilterType)
        ?.instantValue,
    [endDateSearchFilterType, newSearchFilters],
  );

  const dateRangeType = useMemo(
    () =>
      newSearchFilters.find(
        (filter) => filter.type === dateRangeSearchFilterType,
      )?.stringValue as DATE_RANGE_TYPE,
    [dateRangeSearchFilterType, newSearchFilters],
  );

  const onDateFilterChanged = useCallback(
    ({
      type,
      startDate,
      endDate,
    }: {
      type?: DATE_RANGE_TYPE;
      startDate?: Date;
      endDate?: Date;
    }) => {
      setNewSearchFilters((prev) => {
        const _prev = [...prev].filter(
          (filter) =>
            filter.type !== startDateSearchFilterType &&
            filter.type !== endDateSearchFilterType &&
            filter.type !== dateRangeSearchFilterType,
        );
        if (startDate) {
          _prev.push({
            type: startDateSearchFilterType,
            instantValue: startDate.toISOString(),
          });
        }
        if (endDate) {
          _prev.push({
            type: endDateSearchFilterType,
            instantValue: endDate.toISOString(),
          });
        }
        if (type) {
          _prev.push({
            type: dateRangeSearchFilterType,
            stringValue: type,
          });
        }
        return _prev;
      });
    },
    [
      dateRangeSearchFilterType,
      endDateSearchFilterType,
      setNewSearchFilters,
      startDateSearchFilterType,
    ],
  );

  const onDateRangeChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const dateRangeType = e.target.value as DATE_RANGE_TYPE;
      if (!dateRangeType) {
        onDateFilterChanged({});
        return;
      }
      switch (dateRangeType) {
        case DATE_RANGE_TYPE.CUSTOM: {
          onDateFilterChanged({ type: dateRangeType });
          break;
        }
        default: {
          onDateFilterChanged({
            type: dateRangeType,
            startDate: moment(getStartDate(dateRangeType)).toDate(),
            endDate: undefined,
          });
          break;
        }
      }
    },
    [getStartDate, onDateFilterChanged],
  );

  const _startDate = useMemo(
    () => (startDate ? moment(startDate).toDate() : undefined),
    [startDate],
  );

  const _endDate = useMemo(
    () => (endDate ? moment(endDate).toDate() : undefined),
    [endDate],
  );

  const onStartDateChanged = useCallback(
    (date?: Date) => {
      onDateFilterChanged({
        type: DATE_RANGE_TYPE.CUSTOM,
        startDate: date,
        endDate: _endDate,
      });
    },
    [_endDate, onDateFilterChanged],
  );

  const onEndDateChanged = useCallback(
    (date?: Date) => {
      onDateFilterChanged({
        type: DATE_RANGE_TYPE.CUSTOM,
        startDate: _startDate,
        endDate: date,
      });
    },
    [_startDate, onDateFilterChanged],
  );

  return (
    <VStack w="full">
      <HStack w="full">
        {icon}
        <Text
          fontFamily="Outfit"
          color={colors.blackSecondary}
          fontSize={18}
          fontWeight={600}
        >
          {title}
        </Text>
      </HStack>
      <VStack w="full">
        <Select
          placeholder="Select One"
          size="sm"
          rounded="md"
          value={dateRangeType}
          onChange={onDateRangeChange}
        >
          {Object.values(DATE_RANGE_TYPE).map((type, index) => (
            <option key={index} value={type}>
              {titleForDateRangeType(type)}
            </option>
          ))}
        </Select>
        {dateRangeType === DATE_RANGE_TYPE.CUSTOM && (
          <>
            <InputGroup size="sm">
              <InputLeftAddon
                children="Start Date"
                w={90}
                borderLeftRadius="md"
                bg={colors.gray0}
              />
              <DateInput value={_startDate} onChange={onStartDateChanged} />
            </InputGroup>
            <InputGroup size="sm">
              <InputLeftAddon
                children="End Date"
                w={90}
                borderLeftRadius="md"
                bg={colors.gray0}
              />
              <DateInput value={_endDate} onChange={onEndDateChanged} />
            </InputGroup>
          </>
        )}
      </VStack>
    </VStack>
  );
};

export default memo(FiltersDrawerDateRangeSelector);
